import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Globe, UserCheck, Users, Building2, ChevronDown, Send, Info } from 'lucide-react';
import { postsApi } from '../api/client';
import './ShareToFeedModal.css';

// Visibility options in order from most-open to most-restrictive
const ALL_VIS = [
  { value: 'public',      label: 'Public',               Icon: Globe      },
  { value: 'connections', label: 'Connections & Groups',  Icon: UserCheck  },
  { value: 'friends',     label: 'Connections Only',      Icon: Users      },
  { value: 'groups',      label: 'My Groups Only',        Icon: Building2  },
];

/**
 * ShareToFeedModal
 *
 * Props:
 *  type               – post type ('prayer' | 'scripture' | 'prayer_request' | ...)
 *  title              – modal header text
 *  structuredMarker   – JSON marker string appended after the user's comment
 *  prefillComment     – optional pre-filled textarea value
 *  previewLabel       – label shown above the preview card (e.g. "Prayer", "Reference")
 *  previewContent     – text shown in preview card (e.g. the prayer text or verse)
 *  allowedVisibilities – array of allowed visibility values; first is default
 *  visibilityNote     – optional warning/explanation shown above the selector
 *  onClose            – called when modal should close
 *  onSuccess          – called with the created post object
 */
export default function ShareToFeedModal({
  type = 'reflection',
  title = 'Share to Feed',
  structuredMarker = '',
  prefillComment = '',
  previewLabel = '',
  previewContent = '',
  allowedVisibilities = ['public', 'connections', 'friends', 'groups'],
  visibilityNote = '',
  onClose,
  onSuccess,
}) {
  const [comment,    setComment]  = useState(prefillComment);
  const [visibility, setVis]      = useState(allowedVisibilities[0] || 'public');
  const [saving,     setSaving]   = useState(false);
  const [error,      setError]    = useState('');
  const [done,       setDone]     = useState(false);

  const visOptions   = ALL_VIS.filter(v => allowedVisibilities.includes(v.value));
  const currentVis   = visOptions.find(v => v.value === visibility) || visOptions[0];
  const CurrentIcon  = currentVis?.Icon || Globe;

  async function submit() {
    setSaving(true); setError('');
    try {
      let finalContent = comment.trim();
      if (structuredMarker) {
        finalContent = finalContent
          ? `${finalContent}\n${structuredMarker}`
          : structuredMarker;
      }
      if (!finalContent.trim()) {
        setError('Please add a comment or context before sharing.');
        setSaving(false); return;
      }
      const d = await postsApi.create({ type, visibility, content: finalContent });
      setDone(true);
      onSuccess && onSuccess(d.post);
      setTimeout(onClose, 1500);
    } catch(err) {
      setError(err.message || 'Failed to share. Please try again.');
    } finally { setSaving(false); }
  }

  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <span className="modal-title">{title}</span>
          <button className="modal-close" onClick={onClose}><X size={16}/></button>
        </div>

        {done ? (
          <div className="stf-success">Shared to your feed!</div>
        ) : (
          <>
            {visibilityNote && (
              <div className="stf-visibility-note">
                <Info size={14} style={{flexShrink:0, marginTop:1}}/>
                <span>{visibilityNote}</span>
              </div>
            )}
            {error && <div className="form-error">{error}</div>}

            {/* Preview of the content being shared */}
            {previewContent && (
              <div className="stf-preview">
                {previewLabel && <div className="stf-preview-label">{previewLabel}</div>}
                <div className="stf-preview-content">{previewContent}</div>
              </div>
            )}

            {/* Visibility selector */}
            <div className="stf-vis-wrap">
              <label className="post-select-label">Visible to</label>
              <div className="post-select-box" data-label={currentVis?.label || ''}>
                <CurrentIcon size={14} strokeWidth={2}/>
                <select value={visibility} onChange={e => setVis(e.target.value)} className="post-native-select">
                  {visOptions.map(v => (
                    <option key={v.value} value={v.value}>{v.label}</option>
                  ))}
                </select>
                <ChevronDown size={14} className="post-select-chevron"/>
              </div>
            </div>

            {/* Comment textarea */}
            <textarea
              className="create-textarea"
              placeholder="Add a comment… (optional)"
              rows={3}
              value={comment}
              onChange={e => setComment(e.target.value)}
            />

            <div style={{display:'flex', gap:10, justifyContent:'flex-end'}}>
              <button className="btn btn-outline" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" onClick={submit} disabled={saving}>
                {saving
                  ? <span className="spinner"/>
                  : <><Send size={14}/> Share to Feed</>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
